// domain/services/revenueCycleActiveFrom.js

import { toDateStr } from './effectiveDating';

/**
 * The first service date a clinic bills for, and when that answer stops being editable.
 *
 * NOT `billingStartDate`, AND THE NAME IS THE POINT. "Billing" already means something else in this
 * codebase: `billing/{clinicId}` is the Stripe-shaped subscription document, what we charge a
 * CUSTOMER. This is about claims to a PAYER, which is why the context is called revenueCycle and why
 * the add-on key is too. A field called `billingStartDate` sitting on clinic settings would read as
 * the day we started charging them.
 *
 * A FLOOR, AND THE REASON ONE IS NEEDED. Several orgs have been on the platform for years before
 * anything billing-related existed for them. Without a floor, the first projection run would walk
 * that whole history and produce a billing item for every service delivered before the clinic had a
 * charge master, a funding source or a coverage to point at. Every one of those would hold, so it is
 * a great deal of writing to produce noise.
 *
 * IT IS ALSO A REAL FACT rather than a technical bound: the date we agreed to start billing for this
 * clinic. Timely filing means it is sometimes deliberately EARLIER than the day the add-on was
 * switched on, which is why it is a value somebody chooses per clinic and not one derived from the
 * subscription.
 *
 * A CALENDAR DATE, NEVER AN INSTANT. What it is compared against is a service date, which the
 * projector derives as a local calendar date, and calendar-to-calendar is exact. An instant would
 * need a timezone chosen at read time, and whichever one is chosen moves the boundary by a day for
 * somebody. Same reading every other effective bound in this context takes.
 *
 * THE CLOCK IS THE CALLER'S. Every rule here takes its `today` as an argument rather than reading
 * one: a rule that fetches its own as-of is non-deterministic and cannot be tested without freezing
 * time. `todayDateStr` is the edge helper for callers.
 */

/**
 * Has this clinic's billing start date already applied?
 *
 * WHAT IT GATES: the date is editable until it takes effect, and read-only afterwards. Moving a date
 * that has already applied silently re-scopes what gets projected, and items that fall outside the
 * new window are deleted on the next run — including ones a biller may already have exported to a
 * payer. Before the date arrives there is nothing to re-scope, so it can be changed freely.
 *
 * SET IN THE PAST ON PURPOSE IS FINE, and this does not prevent it: a clinic that has agreed to bill
 * ninety days back is entered with a date ninety days back, on a record where nothing is stored yet.
 * What the rule stops is moving one that is already in force.
 *
 * THE TWO UNREADABLE CASES ANSWER DIFFERENTLY, deliberately, because they fail differently.
 *
 *   - An unreadable `today` locks the field. Wrongly locked is visible and somebody reports it;
 *     wrongly editable is the failure nobody notices until items have gone missing. `todayDateStr`
 *     cannot produce one, so this only guards a caller passing something else.
 *   - An unreadable stored date does NOT lock it. There is no usable floor in that state, so nothing
 *     is correctly scoped by it and there is nothing to protect — while locking would leave a
 *     malformed value that no screen can correct.
 *
 * @param {string|null|undefined} revenueCycleActiveFrom - 'YYYY-MM-DD' as stored, or absent
 * @param {string} today - 'YYYY-MM-DD'; see todayDateStr
 * @returns {boolean}
 */
// eslint-disable-next-line import/prefer-default-export
export function hasTakenEffect(revenueCycleActiveFrom, today) {
  const asOf = toDateStr(today);

  if (asOf === null) {
    return true;
  }
  const start = toDateStr(revenueCycleActiveFrom);

  if (start === null) {
    return false;
  }
  return start <= asOf;
} // hasTakenEffect()
